import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useMst } from '../../../models/stores/Root';
import AppliedGiftCardList from '../AppliedGiftCardList';
import GiftCard from '../../input/GiftCard';
import LoadingView from '../../../pages/views/LoadingView';

interface GiftCardSectionProps {
  onGiftCardsUpdating: () => void;
  setApplyButtonEnabled?: (enabled: boolean) => void;
  clearGiftCard?: boolean;
}

const GiftCardSection: React.FC<GiftCardSectionProps> = observer(
  ({
    onGiftCardsUpdating,
    setApplyButtonEnabled,
    clearGiftCard,
  }: GiftCardSectionProps) => {
    const rootStore = useMst();
    const { cart } = rootStore;
    const [isLoading, setIsLoading] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState('');

    const handleApplyGiftCardClicked = async (
      giftCardCode: string
    ): Promise<string | undefined> => {
      setIsLoading(true);
      setLoadingMessage('Applying Gift Card...');
      onGiftCardsUpdating();
      await cart.applyGiftCardFlow(giftCardCode);
      if (cart.giftCardError) {
        setLoadingMessage('');
        setIsLoading(false);
        return cart.giftCardError;
      }
      await rootStore.putStripeIntentFlow();
      setLoadingMessage('');
      setIsLoading(false);
      return undefined;
    };

    const handleRemoveGiftCardClicked = async (
      giftCardCode: string
    ): Promise<void> => {
      setIsLoading(true);
      setLoadingMessage('Removing Gift Card...');
      onGiftCardsUpdating();
      await cart.removeGiftCardFlow(giftCardCode);
      await rootStore.putStripeIntentFlow();
      setLoadingMessage('');
      setIsLoading(false);
    };

    return (
      <>
        <LoadingView isOpen={isLoading} message={loadingMessage} />
        {cart.cartData.giftCards.length > 0 && (
          <div className="text-sm flex flex-col">
            <AppliedGiftCardList
              giftCards={cart.appliedGiftCards}
              onRemoveGiftCardClicked={handleRemoveGiftCardClicked}
            />
            {cart.cartData.totals.grandTotal > 0 &&
              cart.appliedGiftCards.length > 0 && (
                <div className="max-h-9 flex flex-flow">
                  <span className="w-1/12 pl-2 max-w-max">&zwnj;</span>
                  <p className="pl-6">
                    Please choose a payment method above for the remaining
                    balance.
                  </p>
                </div>
              )}
          </div>
        )}
        <GiftCard
          onGiftCardApplyClicked={handleApplyGiftCardClicked}
          appliedGiftCards={cart.appliedGiftCardCodes}
          isApplyingGiftCard={isLoading}
          setApplyButtonEnabled={setApplyButtonEnabled}
          clearGiftCard={clearGiftCard}
        />
      </>
    );
  }
);

export default GiftCardSection;
